import React, { useEffect, useRef, useState } from "react";
import DirectPrintBanner from "../components/DirectPrintBanner";
import QrScanButton from "../components/QrScanButton";
import PaymentModePicker from "../components/PaymentModePicker";
import { useScannerInput } from "../lib/scannerInput";
import { useNavigate } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { printHtml } from "../lib/print";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import SareeSaleReceipt from "../components/SareeSaleReceipt";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ScanLine, Receipt, RotateCcw, FileBarChart, Printer } from "lucide-react";
import { toast } from "sonner";

export default function SareeSalesCounter({ title = "Sarees Contract Sales", subtitle = "Scan the godown token, verify stock, generate the sale invoice.", channel = "CONTRACT" }) {
  const channelLabel = channel === "CONTRACT" ? "Sarees Contract Sales" : "Sarees Devasthanam Sales";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [token, setToken] = useState(null);
  const [temple, setTemple] = useState(null);
  const [sold, setSold] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ devotee_name: "", cell_no: "", payment_mode: "CASH" });
  const receiptRef = useRef(null);

  useEffect(() => {
    api.get("/temples").then((r) => setTemple(r.data.find((t) => t.temple_id === user.temple_id) || r.data[0])).catch(() => {});
  }, []); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const scanner = useScannerInput((v) => { setCode(v); search(v); });
  const search = async (val) => {
    const c = typeof val === "string" ? val : code;
    if (!c.trim()) return;
    setLoading(true);
    setSold(null);
    try {
      const r = await api.get(`/sarees/${encodeURIComponent(c.trim())}`);
      setToken(r.data);
      setForm({ devotee_name: "", cell_no: "", payment_mode: "CASH" }); // the buyer is entered at the counter (the token holds the donor)
    } catch (e) {
      setToken(null);
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  // Rate (from the token), HSN code and GST % (from the saree type) are fixed - not edited at the counter.
  const terms = token?.sale_terms || { rate: Number(token?.rate) || 0, hsn_code: "52082120", gst_percent: 5 };
  const rate = Number(terms.rate) || 0;
  const gstAmt = Math.round(rate * (Number(terms.gst_percent) || 0)) / 100;
  const total = rate + gstAmt;

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/sarees/${token.token_no}/sale`, { ...form, channel });
      setSold(r.data);
      setToken(r.data);
      toast.success(`Invoice generated — Receipt No ${r.data.sale.rect_no} — printing…`);
      setTimeout(() => { if (receiptRef.current) printHtml(receiptRef.current.outerHTML, "receipt-page", `Saree_Receipt_${r.data.sale?.rect_no || r.data.token_no}`); }, 500);
    } catch (e) { toast.error(fmtErr(e)); } finally { setLoading(false); }
  };

  const reset = () => { setToken(null); setSold(null); setCode(""); setForm({ devotee_name: "", cell_no: "", payment_mode: "CASH" }); };

  return (
    <div className="max-w-5xl mx-auto" data-testid="sales-counter-page">
      <DirectPrintBanner />
      <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900">{title}</h1>
          <p className="text-sm text-slate-500">{subtitle}</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/sarees/reports")} data-testid="sales-dcr-btn">
          <FileBarChart className="w-4 h-4 mr-1" /> DCR Report
        </Button>
      </div>

      <div className="scan-row flex gap-2 mb-6">
        <Input ref={scanner.ref} autoFocus data-testid="sales-code-input" value={code} onChange={(e) => setCode(e.target.value)}
          placeholder="Scan QRCode / enter Token No" className="h-12 font-mono" onKeyDown={scanner.onKeyDown} />
        <QrScanButton onScan={(t) => { setCode(t); search(t); scanner.refocus(); }} label="Camera" />
        <Button data-testid="sales-verify-btn" onClick={search} disabled={loading} className="h-12 px-6 bg-maroon-700 hover:bg-maroon-800 text-white">
          <ScanLine className="w-5 h-5 mr-2" /> Verify
        </Button>
        <Button variant="outline" onClick={reset} className="h-12" data-testid="sales-reset-btn"><RotateCcw className="w-4 h-4" /></Button>
      </div>

      {token && (() => {
        const routedHere = (la) => channel === "CONTRACT" ? (la === channelLabel || (la || "").startsWith("Saree Contract Sales")) : (la || "").startsWith("Sarees Devasthanam Sales");
        const saleable = token.status === "GODOWN_IN" || (token.status === "GODOWN_OUT" && token.out_type === "Sale" && routedHere(token.located_at));
        return (
        // One column in the middle while selling; stock + receipt side by side once the invoice is made.
        <div className={sold ? "grid grid-cols-1 lg:grid-cols-2 gap-5" : "max-w-2xl mx-auto"}>
          <div className="space-y-5">
            <Card className="card-gold-top shadow-sm" data-testid="sales-details">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg text-maroon-900">Stock Details</CardTitle>
                <StatusBadge status={token.status} saree />
              </CardHeader>
              <CardContent className="flex gap-4">
                {token.photo ? (
                  <img src={token.photo} alt="saree" className="w-24 h-28 object-cover rounded-md border-2 border-gold-400" data-testid="sales-saree-photo" />
                ) : (
                  <div className="w-24 h-28 rounded-md border-2 border-dashed border-gold-400 flex items-center justify-center text-[10px] text-slate-400">No photo</div>
                )}
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm flex-1">
                  <div><p className="text-xs text-gold-700 font-bold uppercase">Token No</p><p className="font-mono text-xs">{token.token_no}</p></div>
                  <div><p className="text-xs text-gold-700 font-bold uppercase">Category</p><p>{token.category}</p></div>
                  <div><p className="text-xs text-gold-700 font-bold uppercase">Saree Type</p><p>{token.saree_type_name}</p></div>
                  <div><p className="text-xs text-gold-700 font-bold uppercase">Quantity</p><p>{token.quantity || 1}</p></div>
                  <div><p className="text-xs text-gold-700 font-bold uppercase">Rate</p><p>₹{token.rate}</p></div>
                  <div><p className="text-xs text-gold-700 font-bold uppercase">Godown In By</p><p>{token.verified_by || "-"}</p></div>
                </div>
              </CardContent>
            </Card>

            {saleable && !sold && (
              <Card className="card-gold-top shadow-sm" data-testid="sales-form">
                <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">Sale Details</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Buyer Name</Label><Input value={form.devotee_name} onChange={(e) => set("devotee_name", e.target.value)} placeholder="Enter buyer name" name="saree-buyer-name" autoComplete="off" data-lpignore="true" data-testid="sales-devotee-name" /></div>
                  <div><Label className="text-xs">Buyer Cell No</Label><Input value={form.cell_no} inputMode="numeric" maxLength={10} onChange={(e) => set("cell_no", e.target.value.replace(/\D/g, ""))} placeholder="Enter 10-digit mobile" name="saree-buyer-cell" autoComplete="off" data-lpignore="true" data-testid="sales-cell-no" /></div>
                  <div><Label className="text-xs">Rate (₹)</Label><Input value={rate.toLocaleString("en-IN", { minimumFractionDigits: 2 })} readOnly tabIndex={-1} className="bg-slate-100 text-slate-700 cursor-not-allowed" data-testid="sales-rate" /></div>
                  <div><Label className="text-xs">HSN Code</Label><Input value={terms.hsn_code} readOnly tabIndex={-1} className="bg-slate-100 text-slate-700 cursor-not-allowed" data-testid="sales-hsn" /></div>
                  <div><Label className="text-xs">GST %</Label><Input value={`${Number(terms.gst_percent)}%`} readOnly tabIndex={-1} className="bg-slate-100 text-slate-700 cursor-not-allowed" data-testid="sales-gst" /></div>
                  <div className="flex items-end">
                    <div className="w-full bg-gold-50 border border-gold-300 rounded-md px-3 py-2 flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gold-700">Total (auto)</span>
                      <span className="font-heading text-lg font-bold text-maroon-900" data-testid="sales-total">₹{total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                  <div className="col-span-2"><PaymentModePicker value={form.payment_mode} onChange={(v) => set("payment_mode", v)} testid="sales-pay-mode" /></div>
                  <Button onClick={generate} disabled={loading} data-testid="sales-generate-btn"
                    className="col-span-2 h-12 bg-maroon-700 hover:bg-maroon-800 text-gold-100 font-heading tracking-widest">
                    <Receipt className="w-5 h-5 mr-2" /> GENERATE INVOICE
                  </Button>
                </CardContent>
              </Card>
            )}
            {token.status === "ISSUED" && (
              <p className="rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-800" data-testid="sales-not-in">
                This token is not verified into the godown yet. Complete Godown In first.
              </p>
            )}
            {token.status === "GODOWN_OUT" && !saleable && !sold && (
              <p className="rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-800" data-testid="sales-routed-elsewhere">
                {token.out_type === "Sale"
                  ? `This token is routed to ${token.located_at}. Please bill it from that counter.`
                  : `This stock was taken out (${token.out_type}) — not available for sale.`}
              </p>
            )}
            {token.status === "SOLD" && !sold && (
              <p className="rounded-md border border-slate-300 bg-slate-50 p-3 text-sm text-slate-600" data-testid="sales-already-sold">
                Already sold — Receipt No {token.sale?.rect_no} on {token.sale?.sold_at ? new Date(token.sale.sold_at).toLocaleString("en-IN") : "-"}.
              </p>
            )}
          </div>

          {sold && (
            <div className="space-y-3" data-testid="sales-receipt-section">
              <Button onClick={() => receiptRef.current && printHtml(receiptRef.current.outerHTML, "receipt-page", `Saree_Receipt_${sold.sale?.rect_no || sold.token_no}`)}
                className="bg-saffron-500 hover:bg-saffron-600 text-white" data-testid="sales-print-btn">
                <Printer className="w-4 h-4 mr-1" /> Print Receipt Again
              </Button>
              <div className="overflow-x-auto" ref={receiptRef}><SareeSaleReceipt token={sold} temple={temple} /></div>
            </div>
          )}
        </div>
        );
      })()}
    </div>
  );
}
